//@ts-strict-ignore
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
//@ts-ignore
import DatePicker, { registerLocale } from 'react-datepicker';
import InputPrefix from '../../../components/connectedForm/InputPrefix';
import InputBlock from '../../../components/connectedForm/InputBlock';
import InputGroup from '../../../components/connectedForm/InputGroup';
import InputLabel from '../../../components/connectedForm/InputLabel';
import InputErrorMessage from '../../../components/connectedForm/InputErrorMessage';
import { useCompany } from '../../../utils/FetchConfig';
import { ICompany, Maybe, Scalars } from '../../../graphql/schema/p4types';
import {
  add,
  addBusinessDays,
  addDays,
  addMonths,
  addWeeks,
  addYears,
  compareDesc,
  format,
  getDay,
  getYear,
  isValid,
  parse,
  parseISO,
  setHours,
  setMinutes,
  setYear,
  subBusinessDays,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import getValueFromEvent from '../utils/getValue';

import enGB from 'date-fns/locale/en-GB';
import { CustomKeydownInput } from '../../DateInputEditor/CustomKeydownInput';
import ICommonFieldProps from '../../../@types/ICommonFieldProps';

registerLocale('en-GB', enGB);

//String sort on times seems to be inconsistent between browsers, so using this more elaborate but consistent solution.
const sortTimesAndConvertToJavaDates = (stringTimes: Array<string>) => {
  const asDates = stringTimes.map((stringTime) => {
    const splitTime = stringTime.split(':');
    return setHours(setMinutes(new Date(), parseInt(splitTime[1], 10)), parseInt(splitTime[0], 10));
  });

  return asDates.sort(compareDesc);
};

const midnight = setHours(setMinutes(new Date(), 0), 0);

export const getMinTimeDate = (company: Maybe<ICompany>) => {
  if (!company) {
    return midnight;
  }
  const times: Array<Scalars['LocalTime']> = [];
  if (company.sunday?.worked && company.sunday.start) times.push(company.sunday.start);
  if (company.monday?.worked && company.monday.start) times.push(company.monday.start);
  if (company.tuesday?.worked && company.tuesday.start) times.push(company.tuesday.start);
  if (company.wednesday?.worked && company.wednesday.start) times.push(company.wednesday.start);
  if (company.thursday?.worked && company.thursday.start) times.push(company.thursday.start);
  if (company.friday?.worked && company.friday.start) times.push(company.friday.start);
  if (company.saturday?.worked && company.saturday.start) times.push(company.saturday.start);
  const sortedTimes = sortTimesAndConvertToJavaDates(times);
  return times.length ? sortedTimes[times.length - 1] : midnight;
};

export const getMaxTimeDate = (company: Maybe<ICompany>) => {
  if (!company) {
    return midnight;
  }
  const times: Array<Scalars['LocalTime']> = [];
  if (company.sunday?.worked && company.sunday.end) times.push(company.sunday.end);
  if (company.monday?.worked && company.monday.end) times.push(company.monday.end);
  if (company.tuesday?.worked && company.tuesday.end) times.push(company.tuesday.end);
  if (company.wednesday?.worked && company.wednesday.end) times.push(company.wednesday.end);
  if (company.thursday?.worked && company.thursday.end) times.push(company.thursday.end);
  if (company.friday?.worked && company.friday.end) times.push(company.friday.end);
  if (company.saturday?.worked && company.saturday.end) times.push(company.saturday.end);
  const sortedTimes = sortTimesAndConvertToJavaDates(times);
  return times.length ? sortedTimes[0] : midnight;
};

/**
 * Parse "adjustment" date strings, e.g. '+5', '-5w', etc.
 * Only has any effect if the string starts with '+' or '-', if no numeric part is entered will advance by 1 'unit'
 *  (Days by default, but can enter 'y', 'years', 'm', 'months', 'w', or 'weeks' to specify alternatives).
 *
 * @param dateString - A string representing a date in an unknown format.
 */
const getAdjustedDate = (dateString?: string): Date | null => {
  if (!dateString) {
    return null;
  }

  const lDateString = dateString.toLowerCase();
  if (lDateString.startsWith('+') && lDateString.length > 1) {
    const numericString = lDateString.match(/\d+/g)?.[0] ?? '1';
    const numericPart = parseInt(numericString, 10);

    if ((lDateString.endsWith('y') && !lDateString.includes('day')) || lDateString.includes('year')) {
      return addYears(new Date(), numericPart);
    } else if (lDateString.endsWith('m') || lDateString.includes('month')) {
      return addMonths(new Date(), numericPart);
    } else if (lDateString.endsWith('w') || lDateString.includes('week')) {
      return addWeeks(new Date(), numericPart);
    } else if (lDateString.endsWith('b') || lDateString.includes('bus')) {
      return addBusinessDays(new Date(), numericPart);
    }
    return addDays(new Date(), numericPart);
  } else if (lDateString.startsWith('-')) {
    const numericString = lDateString.match(/\d+/g)?.[0] ?? '1';
    const numericPart = parseInt(numericString, 10);

    if ((lDateString.endsWith('y') && !lDateString.includes('day')) || lDateString.includes('year')) {
      return subYears(new Date(), numericPart);
    } else if (lDateString.endsWith('m') || lDateString.includes('month')) {
      return subMonths(new Date(), numericPart);
    } else if (lDateString.endsWith('w') || lDateString.includes('week')) {
      return subWeeks(new Date(), numericPart);
    } else if (lDateString.endsWith('b') || lDateString.includes('bus')) {
      return subBusinessDays(new Date(), numericPart);
    }
    return subDays(new Date(), numericPart);
  }
  return null;
};

/**
 * Parse a date string to a Date, using every format we think a user might attempt to use.
 * Add more as necessary (Taking care where formats clash to put the most likely format first).
 *
 * @param dateString - A string representing a date in an unknown format.
 */
export const getJavaDate = (dateString: string): Date | null => {
  const adjustedDate = getAdjustedDate(dateString);
  if (adjustedDate) {
    return adjustedDate;
  }

  let potentialDate: Date;
  // Note - "Day Only" options are parsed first because 'parseISO' will set the year for some single-digit entries.
  // It's unlikely that someone entering '11' wants the year '1099' rather than '11th of the current month'!
  if (
    isValid((potentialDate = parse(dateString, 'dd', new Date()))) || // 05 (Just the day number)
    isValid((potentialDate = parse(dateString, 'd', new Date()))) || // 5 (Just the day number)
    isValid((potentialDate = parseISO(dateString))) ||
    isValid((potentialDate = parse(dateString, 'dd/MM', new Date()))) || // 05/12
    isValid((potentialDate = parse(dateString, 'dd/MM/yy', new Date()))) || // 05/12/25
    isValid((potentialDate = parse(dateString, 'dd/MM/yyyy', new Date()))) || // 05/12/2025
    isValid((potentialDate = parse(dateString, 'dd.MM.yyyy', new Date()))) || // 05.12.2025
    isValid((potentialDate = parse(dateString, 'dd-MM-yyyy', new Date()))) || // 05-12-2025
    isValid((potentialDate = parse(dateString, 'd/MM', new Date()))) || // 5/12
    isValid((potentialDate = parse(dateString, 'd/MM/yy', new Date()))) || // 5/12/25
    isValid((potentialDate = parse(dateString, 'd/MM/yyyy', new Date()))) || // 5/12/2025
    isValid((potentialDate = parse(dateString, 'dd/M', new Date()))) || // 05/7 (Different example for single-digit month)
    isValid((potentialDate = parse(dateString, 'dd/M/yy', new Date()))) || // 05/7/25 (Different example for single-digit month)
    isValid((potentialDate = parse(dateString, 'd/M', new Date()))) || // 5/7 (Different example for single-digit month)
    isValid((potentialDate = parse(dateString, 'd/M/yy', new Date()))) || // 5/7/25 (Different example for single-digit month)
    isValid((potentialDate = parse(dateString, 'do MMM', new Date()))) || // 5th Dec
    isValid((potentialDate = parse(dateString, 'do MMM yy', new Date()))) || // 5th Dec 25
    isValid((potentialDate = parse(dateString, 'do MMM yyyy', new Date()))) || // 5th Dec 2025
    isValid((potentialDate = parse(dateString, 'do MMMM', new Date()))) || // 5th December
    isValid((potentialDate = parse(dateString, 'do MMMM yy', new Date()))) || // 5th December 25
    isValid((potentialDate = parse(dateString, 'do MMMM yyyy', new Date()))) || // 5th December 2025
    isValid((potentialDate = parse(dateString, 'd MMM', new Date()))) || // 5 Dec
    isValid((potentialDate = parse(dateString, 'd MMM yy', new Date()))) || // 5 Dec 25
    isValid((potentialDate = parse(dateString, 'd MMM yyyy', new Date()))) || // 5 Dec 2025
    isValid((potentialDate = parse(dateString, 'd MMMM', new Date()))) || // 5 December
    isValid((potentialDate = parse(dateString, 'd MMMM yy', new Date()))) || // 5 December 25
    isValid((potentialDate = parse(dateString, 'd MMMM yyyy', new Date()))) || // 5 December 2025
    isValid((potentialDate = parse(dateString, 'iii do MMM', new Date()))) || // Mon 5th Dec
    isValid((potentialDate = parse(dateString, 'iii do MMM yy', new Date()))) || // Mon 5th Dec 25
    isValid((potentialDate = parse(dateString, 'iii do MMM yyyy', new Date()))) || // Mon 5th Dec 25
    isValid((potentialDate = parse(dateString, 'iii do MMMM', new Date()))) || // Mon 5th December
    isValid((potentialDate = parse(dateString, 'iii do MMMM yy', new Date()))) || // Mon 5th December 25
    isValid((potentialDate = parse(dateString, 'iii do MMMM yyyy', new Date()))) || // Mon 5th December 25
    isValid((potentialDate = parse(dateString, 'P', new Date()))) || // Locale Date
    isValid((potentialDate = parse(dateString, 'PP', new Date()))) || // Locale Date
    isValid((potentialDate = parse(dateString, 'PPP', new Date()))) || // Locale Date
    isValid((potentialDate = parse(dateString, 'yyyy/MM/dd', new Date()))) || // 2025/12/05
    isValid((potentialDate = parse(dateString, 'yyyy.MM.dd', new Date()))) || // 2025/12/05
    isValid((potentialDate = parse(dateString, 'yyyy-MM-dd', new Date()))) // 2025/12/05
  ) {
    return potentialDate;
  }
  return null;
};

export type IDateFieldProps = ICommonFieldProps & {
  active?: boolean;
  id?: string;
  dateFormat?: string;
  portal?: boolean;
  label?: string;
  showTimeSelect?: boolean;
  validatePostingDate?: boolean;
  validate?: (dataid: string, value: any, props: any) => void;
  includeLabel?: boolean;
  excludeDays?: Array<number>;
  hint?: string;
  inputPrefix?: string;
  flexBox?: any;
  readOnly?: boolean;
  mandatory?: boolean;
  validationErrors?: any;
  showWhenNotActive?: boolean;
  touched?: boolean;
  customValidationErrors?: any;
  view?: any;
  onBlur?: (value?: any) => void;
  onChange?: (value?: any) => void;
  inGrid?: boolean;
  value?: any; //TODO - Improve type
  defaultValue?: any; //TODO - Improve type
  disabled?: boolean;
  custom?: any;
  testId?: string; //TODO - Replace with data-testid
  refocusInputOnCalendarClose?: boolean;
  reformatLabel?: boolean;
  placeholder?: string;
  allowSameDay?: boolean;
  minDate?: Date;
  maxDate?: Date;
  isClearable?: boolean;
};

const DateField = (props: IDateFieldProps) => {
  const value = props.value || props.defaultValue || '';

  const initialDate = useMemo(() => parseISO(value), [value]);
  const [date, setDate] = useState<Date | null>(isValid(initialDate) ? initialDate : new Date());
  const [inputString, setInputString] = useState('');
  const datePickerRef = useRef<any>();
  const inputRef = useRef<any>();

  const { readOnly, showTimeSelect, testId, refocusInputOnCalendarClose = false } = props;

  useEffect(() => {
    if (isValid(initialDate)) {
      setDate(initialDate);
      setInputString(format(initialDate, 'do MMM yyyy'));
    } else {
      setDate(null);
      setInputString('');
    }
  }, [initialDate]);

  const company: Maybe<ICompany> = useCompany() as Maybe<ICompany>;
  const dateServiceFormat = props.showTimeSelect ? `yyyy-MM-dd'T'HH:mm` : 'yyyy-MM-dd';

  const onChange = (value: any) => {
    if (readOnly) return;
    if (isValid(value)) {
      const commit = format(value, dateServiceFormat);
      props.onChange?.(commit);
    } else {
      const parsedDate = getJavaDate(value);
      const commit = parsedDate && isValid(parsedDate) ? format(parsedDate, 'yyyy-MM-dd') : null;
      props.onChange?.(commit);
    }
  };

  const onBlur = () => {
    if (readOnly) return;
    props.onBlur?.(date ? format(date, dateServiceFormat) : null);
  };

  const getMinTime = () => {
    return getMinTimeDate(company);
  };

  const getMaxTime = () => {
    return getMaxTimeDate(company);
  };

  const checkDocumentDateLimits = () => {
    if (!props.custom?.validatePostingDate || !company) {
      return false;
    }
    return company.limitDocumentDate;
  };

  const getMinDate = () => {
    if (checkDocumentDateLimits()) {
      return subDays(new Date(), company?.documentDateMaxDaysBefore);
    }
    return null;
  };

  const getMaxDate = () => {
    if (checkDocumentDateLimits()) {
      return addDays(new Date(), company?.documentDateMaxDaysAfter);
    }
    return null;
  };

  const excludeDays = props.excludeDays!; //Gets a default of [] if not provided.
  const startDate = getMinDate();
  const endDate = getMaxDate();
  const minTime = getMinTime();
  const maxTime = getMaxTime();

  const filterDate =
    excludeDays.length === 0 && !props.minDate && !props.maxDate
      ? undefined
      : (date: Date) => {
          const afterMinDate = props.minDate ? date >= props.minDate : true;
          const beforeMaxDate = props.maxDate ? date <= props.maxDate : true;
          const notExcludedDay = excludeDays.length === 0 || !excludeDays.includes(getDay(date));
          return afterMinDate && beforeMaxDate && notExcludedDay;
        };

  const onChangeRaw = (event: any) => {
    if (readOnly) return;
    const value = getValueFromEvent(event);
    setInputString(value);
  };

  const onKeyDown = (event: any, baseOnKeyDown: Function) => {
    if (readOnly) return;
    switch (event.keyCode) {
      case 9: //TAB
      case 13: //ENTER
        let newDate = date;

        const dateString = getValueFromEvent(event);
        const potentialDate = getJavaDate(dateString);

        //If user enters a 'day/month' date - Coerce it to standard format.
        if (potentialDate) {
          newDate = potentialDate;
        } else if (inputString.toLowerCase() === 't') {
          newDate = new Date();
        } else if (/^([+-])\d{1,6}([dwm])?$/i.test(inputString.trim())) {
          const units = inputString.slice(-1).toLowerCase();
          let off;
          if (units === 'm') {
            off = inputString.slice(0, -1);
            off = parseInt(off, 10);
            //Go one day before the target, then add one *business* day so we skip over weekend as appropriate.
            newDate = add(new Date(), { months: off, days: -1 });
            newDate = addBusinessDays(newDate, 1);
          } else if (units === 'w') {
            off = inputString.slice(0, -1);
            off = parseInt(off, 10);
            newDate = add(new Date(), { weeks: off });
          } else {
            off = units === 'd' ? inputString.slice(0, -1) : inputString;
            newDate = add(new Date(), { days: off as unknown as number });
          }
        } else if (newDate?.getFullYear() === 2001) {
          //If user enters only day/month, the year defaults to 2001 - But we want it to use the current year.
          newDate = setYear(newDate, getYear(new Date()));
        }

        if (isValid(newDate)) {
          onChange(newDate);
          setDate(newDate); //Set the local state to the new date, so that if/when a blur occurs, it has an up-to-date value.
        }
        datePickerRef.current?.setOpen?.(false);
        break;
      default:
        baseOnKeyDown(event);
    }
  };

  const dataTestId = props['data-testid'] || testId || props.id; //Fallback to id where testId is not provided.

  return (
    <div data-testid={dataTestId}>
      <DatePicker
        ref={datePickerRef}
        dateFormat={props.dateFormat || (props.showTimeSelect ? 'do MMM yyyy HH:mm' : 'do MMM yyyy')}
        selected={date}
        value={date}
        onChange={onChange}
        onChangeRaw={onChangeRaw}
        onBlur={onBlur}
        showTimeSelect={showTimeSelect}
        showTimeInput={showTimeSelect}
        customInput={
          <CustomKeydownInput
            inputRef={inputRef}
            customOnKeydown={onKeyDown}
            highlightTextOnMount={false}
            data-testid={`${dataTestId}-input`}
          />
        }
        isClearable={props.isClearable ?? !readOnly}
        locale={'en-GB'}
        startDate={startDate}
        endDate={endDate}
        placeholderText={''}
        timeFormat={'HH:mm'}
        readOnly={readOnly}
        showYearDropdown={true}
        minTime={minTime}
        maxTime={maxTime}
        portalId={`date-portal-${dataTestId}`}
        disabled={props.disabled || props.readOnly}
        onCalendarClose={() => {
          if (refocusInputOnCalendarClose) {
            inputRef.current?.focus?.();
          }
        }}
        filterDate={filterDate}
      />
    </div>
  );
};

DateField.propTypes = {
  active: PropTypes.bool,
  allowSameDay: PropTypes.bool,
  clearButton: PropTypes.bool,
  mandatory: PropTypes.bool,
  onChange: PropTypes.func,
  includeLabel: PropTypes.bool,
  inputPrefix: PropTypes.string,
  readOnly: PropTypes.bool,
  flexBox: PropTypes.bool,
  labelCols: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inputCols: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.string,
  hint: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  validationErrors: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  showWhenNotActive: PropTypes.bool,
  label: PropTypes.string,
  type: PropTypes.string,
  portal: PropTypes.bool,
  excludeDays: PropTypes.array,
};
DateField.defaultProps = {
  active: false,
  allowSameDay: true,
  mandatory: false,
  clearButton: false,
  placeholder: '',
  portal: false,
  onChange: () => {},
  onBlur: () => {},
  includeLabel: true,
  inputPrefix: null,
  readOnly: false,
  flexBox: true,
  labelCols: 1 / 4,
  inputCols: 3 / 4,
  value: '',
  validationErrors: [],
  hint: '',
  showWhenNotActive: false,
  type: 'text',
  label: '',
  id: '',
  excludeDays: [],
};

export default (props: IDateFieldProps) => {
  if (props.inGrid) return <DateField {...props} />;

  const {
    active,
    id,
    label,
    includeLabel,
    inputPrefix,
    flexBox,
    labelCols,
    inputCols,
    readOnly,
    mandatory,
    hint,
    validationErrors,
    showWhenNotActive,
    touched,
    customValidationErrors,
    view,
    reformatLabel,
  } = props;

  const labelProps = {
    flexBox,
    id,
    includeLabel,
    hint,
    label,
    mandatory,
    readOnly,
    view,
    reformatLabel,
  };

  return (
    <InputBlock flexBox={flexBox}>
      <InputLabel {...labelProps} cols={labelCols} />
      <InputGroup flexBox={flexBox} cols={inputCols} withLabel={includeLabel} view={view}>
        <InputPrefix inputPrefix={inputPrefix} />
        <DateField
          {...props}
          onBlur={(value) => {
            props.onBlur?.(value);
          }}
        />
        <InputErrorMessage
          label={label}
          active={active}
          touched={touched}
          validationErrors={validationErrors}
          showWhenNotActive={showWhenNotActive}
          customValidationErrors={customValidationErrors}
        />
      </InputGroup>
    </InputBlock>
  );
};
